import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch } from './api';
import { dealLabel } from './lib/dealLabel';
import { BILLING_LABELS, type BillingType } from './lib/billingType';
import { EditTimeEntry } from './EditTimeEntry';
import type { EditTimeEntryDeal, EditTimeEntryEntry } from './EditTimeEntry';
import { RecentEntriesSkeleton } from './components/Skeleton';

interface RecentEntryNote {
  id: string;
  note: string;
  createdAt: string;
}

interface RecentEntry {
  id: string;
  dealId: string;
  projectId: string | null;
  startedAt: string;
  minutes: number;
  note: string | null;
  notes: RecentEntryNote[];
  deal: { jobNumber: string | null; title: string } | null;
  billingType: BillingType;
}

interface RecentEntriesProps {
  employeeId: string;
  // (employee, billing_type) pairs the rate sheet marks as flat. Used to
  // render those entries as units ("1 inspection") instead of minutes,
  // so employees never see the hourly conversion of a flat-billed entry.
  flatBillingTypes: string[];
  isSupervisor: boolean;
}

const DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const NOTE_TIME_FMT = new Intl.DateTimeFormat('en-CA', {
  hour: 'numeric',
  minute: '2-digit',
});

function formatStartedAt(iso: string): string {
  return DATE_FMT.format(new Date(iso));
}

export function RecentEntries({ employeeId, flatBillingTypes, isSupervisor }: RecentEntriesProps): JSX.Element {
  const flatSet = new Set(flatBillingTypes);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notesOpenId, setNotesOpenId] = useState<string | null>(null);

  const recentQuery = useQuery({
    queryKey: ['recent', employeeId],
    queryFn: () =>
      apiFetch<RecentEntry[]>(`/time-entries?employeeId=${encodeURIComponent(employeeId)}`),
  });
  const dealsQuery = useQuery({
    queryKey: ['deals'],
    queryFn: () => apiFetch<EditTimeEntryDeal[]>('/deals'),
  });

  const entries = recentQuery.data ?? [];
  const deals = dealsQuery.data ?? [];

  if (recentQuery.isLoading) {
    return <RecentEntriesSkeleton />;
  }
  if (entries.length === 0) {
    return (
      <section className="py-3">
        <h2 className="text-sm uppercase tracking-wide font-semibold text-brand-charcoal">Recent</h2>
        <p className="mt-2 text-sm text-brand-slate">No entries in the last 14 days.</p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-2 pb-6">
      <h2 className="text-sm uppercase tracking-wide font-semibold text-brand-charcoal">Recent</h2>
      <ul className="flex flex-col gap-2">
        {entries.map((e) => {
          const label = e.deal
            ? dealLabel({ jobNumber: e.deal.jobNumber, title: e.deal.title })
            : '— (deleted deal)';
          const isEditing = editingId === e.id;
          const editEntry: EditTimeEntryEntry = {
            id: e.id,
            dealId: e.dealId,
            projectId: e.projectId,
            startedAt: e.startedAt,
            minutes: e.minutes,
            note: e.note,
            notes: e.notes ?? [],
            billingType: e.billingType,
          };
          const noteCount = e.notes?.length ?? 0;
          const notesOpen = notesOpenId === e.id;
          return (
            <li
              key={e.id}
              className="rounded-lg bg-brand-steel p-3 ring-1 ring-brand-steel"
            >
              {(() => {
                const isFlat = flatSet.has(e.billingType);
                // Employees never see the time-equivalent of a flat-rate
                // entry. They see "1 Inspection" (the unit). Supervisors
                // see "1 Inspection (2.0h equiv)" alongside, for billing
                // sanity-checks.
                const primary = isFlat
                  ? `1 ${BILLING_LABELS[e.billingType]}`
                  : `${e.minutes}m`;
                const hourEquivLabel = isFlat && isSupervisor
                  ? ` · ${(e.minutes / 60).toFixed(e.minutes >= 600 ? 1 : 2)}h equiv`
                  : '';
                return (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-base font-semibold tabular-nums text-brand-charcoal">{primary}</span>
                    <span className="truncate text-sm text-brand-charcoal">{label}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-brand-slate">
                    <span>{formatStartedAt(e.startedAt)}{hourEquivLabel}</span>
                    {!isFlat && (
                      <span
                        className="rounded-full bg-brand-blue/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-blue ring-1 ring-brand-blue/30"
                      >
                        {BILLING_LABELS[e.billingType]}
                      </span>
                    )}
                    {e.note && noteCount === 0 ? <span>· {e.note}</span> : null}
                  </div>
                  {noteCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setNotesOpenId(notesOpen ? null : e.id)}
                      aria-expanded={notesOpen}
                      className="mt-1 rounded-md bg-brand-charcoal/5 px-2 py-0.5 text-[11px] text-brand-slate hover:bg-brand-charcoal/10"
                    >
                      {notesOpen ? 'Hide' : 'Show'} {noteCount} {noteCount === 1 ? 'note' : 'notes'}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setEditingId(isEditing ? null : e.id)}
                  className="shrink-0 rounded-md bg-brand-blue px-3 py-1 text-xs font-semibold text-brand-white hover:bg-[#0067BB]"
                >
                  {isEditing ? 'Close' : 'Edit'}
                </button>
              </div>
                );
              })()}
              {notesOpen && noteCount > 0 && (
                <ul className="mt-2 flex flex-col gap-1 border-t border-brand-charcoal/10 pt-2">
                  {e.notes.map((n) => (
                    <li key={n.id} className="flex items-start gap-2 text-xs">
                      <span className="font-mono tabular-nums text-brand-slate shrink-0">
                        {NOTE_TIME_FMT.format(new Date(n.createdAt))}
                      </span>
                      <span className="text-brand-charcoal">{n.note}</span>
                    </li>
                  ))}
                </ul>
              )}
              {isEditing && (
                <EditTimeEntry
                  entry={editEntry}
                  employeeId={employeeId}
                  deals={deals}
                  onClose={() => setEditingId(null)}
                  flatBillingTypes={flatBillingTypes}
                  isSupervisor={isSupervisor}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
